/**
 * `@instanexus/authz` — the authorization core (docs/005-authorization.md).
 *
 * **Permissions and roles are code. Only assignments are data.** The prior
 * attempt stored them as rows and paid for it with ~30 repair scripts; this
 * package is the fix, and it is the subsystem docs/005 says S2 ships with or
 * does not ship.
 *
 * **Pure and isomorphic.** No Express, no Prisma, no `@instanexus/db`, no I/O,
 * no clock. `apps/platform-admin` imports it to decide what to render from the
 * same code path the API enforces with — one vocabulary, not two. The Express
 * half of docs/005's five-function interface (`requirePermission`,
 * `publicRoute`) lives in `apps/api`, because those are HTTP concerns.
 *
 * The dependency on `@instanexus/contracts` is one-directional: contracts must
 * never import authz.
 */
import { assertRolesAreLive } from './roles.js';
/**
 * Fail at import, not at the first request. A dead or misspelled role pattern —
 * or one reaching a cross-tenant operator capability — kills the process here
 * (docs/005 §Enforcement, "Role patterns are live").
 */
assertRolesAreLive();
/**
 * The permission vocabulary. `Permission` is a union of string literals, so a
 * typo at a call site does not compile — docs/005 §Enforcement, "Permission
 * strings are valid".
 */
export { CONTROL_TOWER_PERMISSION, DEVICE_ONLY_PERMISSIONS, MANAGE_IMPLIES, OPERATOR_ONLY_PERMISSIONS, PARTNER_ONLY_PERMISSIONS, PARTNER_SETUP_EXCLUDED_PERMISSIONS, PERMISSIONS, SUPPORT_WRITE_PERMISSIONS, PERMISSION_ACTIONS, PERMISSION_APPS, PERMISSION_SET, isPermission, permissionAction, } from './permissions.js';
/**
 * Assignment scopes. `SCOPE_TYPES` must stay identical, in members and order, to
 * the Prisma `enum ScopeType` in `packages/db`.
 */
export { SCOPE_TYPES } from './scope.js';
/**
 * Roles and their expansion. `permissionsForRole` / `permissionsForDeviceRole`
 * **fail closed** on an unknown key — an assignment whose role has been deleted
 * from code grants nothing rather than throwing. `roleKeySchema` is the write-side
 * validator for `RoleAssignment.roleKey`, and `assertRolesAreLive` is the startup
 * assertion (already invoked above; exported so tests can drive it with a
 * fabricated map).
 */
export { DEVICE_ROLES, EMPLOYEE_TIER_ROLE_KEYS, PARTNER_ROLES, PARTNER_ROLE_KEYS, PARTNER_SETUP_ROLES, PARTNER_SETUP_ROLE_KEYS, ROLE_KEYS, ROLE_TIERS, SYSTEM_ROLES, assertRolesAreLive, isEmployeeTierRoleSet, partnerRoleKeySchema, permissionsForDeviceRole, permissionsForPartnerRole, permissionsForRole, roleKeySchema, } from './roles.js';
/**
 * The decisions. `can` and `assertCan` answer "may this principal do this at
 * all"; `allowedLocationIds` answers "over which rows" and its `null` (all) vs
 * `[]` (none) distinction is a data-leak boundary — read its doc comment before
 * writing a list endpoint.
 */
export { AuthorizationDeniedError, allowedLocationIds, allowedLocationIdsForAny, assertCan, can, canAny, } from './can.js';
/**
 * Role-set rules (S42-m4dr): the scopes each role may be granted at, the structural validator
 * for a member's role set, and the no-escalation grant rule. The API enforces them; the admin
 * SPA calls the same functions to decide what to render.
 */
export { ROLE_SCOPES, allowedScopesForRole, assignmentKey, canGrantAssignment, isRoleKey, validateRoleSet, } from './grant.js';
/**
 * Partner team rules (S46-f22b-TASK-062): which partner role may assign which, and the single
 * decision for changing or removing a partner member. The API enforces them; the portal calls the
 * same functions to decide what to render.
 */
export { canAssignPartnerRole, decidePartnerMemberChange } from './partner-team.js';
//# sourceMappingURL=index.js.map